import { useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { AUTH_ACTIONS } from "../utils/authNavigation";

/**
 * After login, resume a protected action (call/chat) on the same page.
 * Hire/book flows are routed to /marketplace/booking from Login.js directly.
 */
export function useResumeProtectedAction({
  enabled = true,
  getCaregiverById,
  onResumeCall,
  onResumeChat,
}) {
  const location = useLocation();
  const navigate = useNavigate();
  const consumedRef = useRef(false);

  useEffect(() => {
    if (!enabled || consumedRef.current) return;

    const { resumeAction, caregiverId, hireCaregiverId } = location.state || {};
    const action = resumeAction;
    const id = caregiverId || hireCaregiverId;
    if (!action || !id) return;

    if (action === AUTH_ACTIONS.HIRE || action === AUTH_ACTIONS.BOOK) {
      consumedRef.current = true;
      navigate("/marketplace/booking", {
        replace: true,
        state: { hireCaregiverId: id },
      });
      return;
    }

    const caregiver = getCaregiverById?.(id);
    if (!caregiver && (action === AUTH_ACTIONS.CALL || action === AUTH_ACTIONS.CHAT)) {
      return;
    }

    consumedRef.current = true;
    const nextState = { ...(location.state || {}) };
    delete nextState.resumeAction;
    delete nextState.caregiverId;
    delete nextState.hireCaregiverId;
    navigate(location.pathname + location.search, {
      replace: true,
      state: Object.keys(nextState).length ? nextState : null,
    });

    if (action === AUTH_ACTIONS.CALL) {
      onResumeCall?.(caregiver || { id });
    } else if (action === AUTH_ACTIONS.CHAT) {
      onResumeChat?.(caregiver || { id });
    }
  }, [
    enabled,
    location.pathname,
    location.search,
    location.state,
    getCaregiverById,
    navigate,
    onResumeCall,
    onResumeChat,
  ]);
}
